import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, AlertTriangle, FileText, ExternalLink,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { v2Api } from "@/lib/v2Api";

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with",
  "is","are","was","were","be","been","has","have","had","that","this",
  "it","its","by","from","as","if","not","no","so","do","does","did",
  "we","our","your","their","all","any","each","such","than","into",
  "he","she","they","you","me","my","us","will","may","can","per","etc",
]);

/** Extract significant words from text for matching against PDF words. */
function significantWords(text) {
  if (!text) return new Set();
  return new Set(
    text
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .split(/\s+/)
      .map((w) => w.toLowerCase())
      .filter((w) => w.length >= 4 && !STOP_WORDS.has(w)),
  );
}

/**
 * SectionPdfPane — right-side panel showing source PDF pages for a section,
 * with word-level highlights drawn over the page image for words that appear
 * in the section title or body.
 */
export default function SectionPdfPane({ rfpId, section }) {
  const [token, setToken]               = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [page, setPage]                 = useState(null);
  const [boxes, setBoxes]               = useState(null);   // word-box data for current page
  const [boxesLoading, setBoxesLoading] = useState(false);

  const pageStart = section != null ? (section.page_start ?? 0) + 1 : 1;
  const pageEnd   = section != null
    ? Math.max(pageStart, (section.page_end ?? section.page_start ?? 0) + 1)
    : 1;

  // Mint auth token whenever the selected section changes.
  useEffect(() => {
    if (!section || !rfpId) return;
    setPage(pageStart);
    setBoxes(null);
    setToken(null);
    setTokenLoading(true);
    let cancelled = false;
    v2Api.rfp.fileToken(rfpId)
      .then((tk) => { if (!cancelled) setToken(tk); })
      .catch((e) => {
        if (!cancelled)
          toast.error(e?.response?.data?.detail ?? e?.message ?? "Couldn't load PDF");
      })
      .finally(() => { if (!cancelled) setTokenLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id, rfpId]);

  // Fetch word boxes whenever page or token changes.
  useEffect(() => {
    if (!token || !page || !rfpId) return;
    let cancelled = false;
    setBoxes(null);
    setBoxesLoading(true);
    v2Api.rfp.wordBoxes(rfpId, page, token)
      .then((data) => { if (!cancelled) setBoxes(data); })
      .catch(() => { if (!cancelled) setBoxes({ words: [] }); })
      .finally(() => { if (!cancelled) setBoxesLoading(false); });
    return () => { cancelled = true; };
  }, [token, page, rfpId]);

  // Build the set of words to highlight from section title + body.
  const highlightSet = useMemo(() => {
    if (!section) return new Set();
    const words = new Set([
      ...significantWords(section.title),
      ...significantWords(section.body_preview || section.summary || ""),
    ]);
    return words;
  }, [section?.id, section?.title, section?.body_preview, section?.summary]);

  const openFullPdf = async () => {
    try { await v2Api.rfp.openFileInNewTab(rfpId, pageStart); }
    catch { toast.error("Couldn't open PDF"); }
  };

  if (!section) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg bg-slate-50
                      flex flex-col items-center justify-center gap-3 text-center p-8"
           style={{ height: "100%" }}>
        <FileText className="w-10 h-10 text-slate-200" />
        <p className="text-sm text-slate-400 max-w-[160px]">
          Click any section card to view its PDF pages here
        </p>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden flex flex-col"
         style={{ height: "100%" }}>

      {/* Header */}
      <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between gap-2 shrink-0">
        <p className="text-[11px] text-slate-400">
          {pageEnd > pageStart ? `Pages ${pageStart}–${pageEnd}` : `Page ${pageStart}`}
          {" "}· source PDF
          {highlightSet.size > 0 && (
            <span className="ml-1 text-amber-500">· section words highlighted</span>
          )}
        </p>
        <Button size="sm" variant="ghost" onClick={openFullPdf}
                className="h-7 text-xs gap-1 shrink-0 text-slate-500">
          <ExternalLink className="w-3 h-3" /> Open full PDF
        </Button>
      </div>

      {/* Page nav */}
      {pageEnd > pageStart && (
        <div className="px-3 py-1.5 border-b flex items-center gap-2 bg-white shrink-0">
          <Button size="sm" variant="outline" className="h-6 w-6 p-0"
                  disabled={!page || page <= pageStart}
                  onClick={() => setPage((p) => Math.max(pageStart, (p ?? pageStart) - 1))}>
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <span className="text-xs text-slate-500 flex-1 text-center">
            Page {page ?? pageStart} · {pageEnd - pageStart + 1} pages in section
          </span>
          <Button size="sm" variant="outline" className="h-6 w-6 p-0"
                  disabled={!page || page >= pageEnd}
                  onClick={() => setPage((p) => Math.min(pageEnd, (p ?? pageStart) + 1))}>
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Page image + highlights */}
      <div className="flex-1 overflow-y-auto p-3">
        {tokenLoading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}
        {!tokenLoading && token && page != null && (
          <PageWithHighlights
            rfpId={rfpId}
            page={page}
            token={token}
            boxes={boxes}
            highlightSet={highlightSet}
            boxesLoading={boxesLoading}
          />
        )}
      </div>
    </div>
  );
}


function PageWithHighlights({ rfpId, page, token, boxes, highlightSet, boxesLoading }) {
  const [errored, setErrored] = useState(false);
  const src = v2Api.rfp.pageImageUrl(rfpId, page, token);

  // Reset error state when page changes.
  useEffect(() => { setErrored(false); }, [page, rfpId]);

  // Compute which word boxes should be highlighted.
  const highlights = useMemo(() => {
    if (!boxes?.words?.length || !highlightSet.size) return [];
    return boxes.words.filter(
      (w) => highlightSet.has(w.t.toLowerCase().replace(/[^a-z0-9]/g, "")),
    );
  }, [boxes, highlightSet]);

  if (errored) {
    return (
      <div className="p-8 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        Couldn't render page {page}
      </div>
    );
  }

  return (
    <div className="relative border border-slate-100 rounded shadow-sm overflow-hidden">
      <img
        src={src}
        alt={`Page ${page}`}
        onError={() => setErrored(true)}
        className="w-full block"
        draggable={false}
      />

      {/* Highlight overlay — absolutely positioned over the image */}
      {!boxesLoading && highlights.map((w, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left:   `${w.x0 * 100}%`,
            top:    `${w.y0 * 100}%`,
            width:  `${(w.x1 - w.x0) * 100}%`,
            height: `${(w.y1 - w.y0) * 100}%`,
            background: "rgba(251, 191, 36, 0.45)",
            borderRadius: 2,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Subtle loading indicator while boxes fetch */}
      {boxesLoading && (
        <div className="absolute top-2 right-2 bg-white/80 rounded px-1.5 py-0.5
                        text-[10px] text-slate-500 flex items-center gap-1">
          <Loader2 className="w-2.5 h-2.5 animate-spin" /> highlighting…
        </div>
      )}
    </div>
  );
}
